import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MdDialog, MdDialogConfig, MdDialogRef } from '@angular/material';

import { ActionType } from 'app/model/constant';
import { AppConfigService } from 'app/services/app-config/app-config.service';
import { Billing } from 'app/model/billing/billing.model';
import { BillingForm } from './billing.form';
import { DialogConfirmComponent } from 'app/components/modal/dialog-confirm/dialog-confirm.component';
import { DialogGeneralMessageComponent } from 'app/components/modal/dialog-general-message/dialog-general-message.component';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';
import { UserService } from 'app/services/user/user.service';
import { DialogConfirmType } from 'app/components/modal/dialog-confirm/dialog-confirm.model';

@Component({
  selector: 'billing-form',
  templateUrl: './billing-form.component.html',
  styleUrls: ['./billing-form.component.scss']
})
export class BillingFormComponent implements OnInit, OnDestroy {

  @Input() options = {};
  @Input() refName = 'form-add';
  @Input() data: any;
  @Input() isDisplayButton = true;
  @Output() onSaveCompleted: EventEmitter<any> = new EventEmitter();
  @Output() onCancelCompleted: EventEmitter<any> = new EventEmitter();

  @ViewChild('name') inputName: ElementRef;
  @ViewChild('phone') inputPhone: ElementRef;
  @ViewChild('address') inputAddress: ElementRef;

  private form: FormGroup;
  private subscriptionForm: Subscription;
  private subscriptionProcess: Subscription;
  private isLoading: Boolean = false; // tslint:disable-line

  constructor(
    private formProvider: BillingForm,
    private formBuilder: FormBuilder,
    private userService: UserService,
    private appConfigService: AppConfigService,
    private dialog: MdDialog,
  ) { }

  ngOnInit() {
    this.buildForm();
  }

  ngOnDestroy() {
    this.subscriptionForm && this.subscriptionForm.unsubscribe();
    this.subscriptionProcess && this.subscriptionProcess.unsubscribe();
  }

  private buildForm() {
    const { getInitForm } = this.formProvider;
    const { billingName: name, billingPhone: phone, billingAddress: address } = this.data;
    this.form = this.formBuilder.group(getInitForm(name, phone, address));
    this.subscriptionForm = this.form.valueChanges.subscribe((data) => {
      this.formProvider.clearErrorMessage(data);
    });
  }

  private onSaveBillingCompleted(billing, state = ActionType.normalSave) {
    this.isLoading = false;
    this.onSaveCompleted.emit({billing, state});
  }

  private onSaveBillingFailure(errors) {
    this.isLoading = false;
    const title = this.appConfigService.getTranslate('checkout-3-address-edit-failure-title');
    const message = this.appConfigService.getTranslate('checkout-3-billing-failure-text-try-again');
    const mainBtn = this.appConfigService.getTranslate('btn-ok');
    const config = {
      data: { icon: 'icon-service-failed', title, message, mainBtn }
    };
    this.dialog.open(DialogGeneralMessageComponent, config);
  }

  // Method for formControl, check value on blur
  // tslint:disable:no-unused-variable
  private isInvalid(field) {
    return this.form.get(field).invalid && this.form.get(field).touched;
  }

  /**
   ***************** UI Event Handler ******************************
   */
  private getSavingProcess() {
    this.isLoading = true;
    this.subscriptionProcess && this.subscriptionProcess.unsubscribe();
    return this.isEditMode() ? this.updateBilling$() : this.addBilling$();
  }

  private onSubmitButtonClicked(event) {
    if (this.form.invalid) {
      this.setFocusErrors(true);
      return false;
    }

    const process$ = this.getSavingProcess();
    this.subscriptionProcess = process$.subscribe(
      this.onSaveBillingCompleted.bind(this),
      this.onSaveBillingFailure.bind(this),
      () => { this.isLoading = false; }
    );
  }

  private onCancelButtonClicked(event) {
    this.isFormChanged() ? this.notifyConfirmFormCancel() : this.onCancelCompleted.emit();
  }

  private isAddBillingForm() {
    return this.refName === 'form-add';
  }

  isFormChanged() {
    let formChanged = this.form.dirty;
    if (this.isAddBillingForm()) {
      const currentBilling = this.getFormBillingData();
      const isBillingEmpty = (
        currentBilling.billingAddress === '' &&
        currentBilling.billingName === '' &&
        currentBilling.billingPhone === ''
      );
      formChanged = (formChanged && !isBillingEmpty);
    }
    return formChanged;
  }

  private notifyConfirmFormCancel() {
    const dialogRef: MdDialogRef<DialogConfirmComponent> = this.dialog.open(DialogConfirmComponent, { data: { confirmType: DialogConfirmType.CONFIRM_CLOSE } });
    dialogRef.afterClosed().subscribe((res) => {
      res && this.onCancelCompleted.emit();
    });
  }

  /**
   ************************************************************
   */
  setSubmitting$(): Observable<any> {
    if (this.form.invalid) {
      this.setFocusErrors();
      return Observable.of(false);
    }

    return this.getSavingProcess()
    .do(
      (billing) => {
        this.onSaveBillingCompleted(billing, ActionType.autoSave);
      },
      (error) => {
        this.onSaveBillingFailure(error);
        return Observable.throw(new Error('add billing failure'));
      }
    )
    .map(() => Observable.of(true));
  }

  setFocusErrors(isFocus = false, form: FormGroup = this.form) {
    const fieldSorted = [
      { name: 'name', element: this.inputName },
      { name: 'phone', element: this.inputPhone },
      { name: 'address', element: this.inputAddress },
    ];

    this.formProvider.setErrorMessage(form);
    fieldSorted.map(field => ({ control: form.get(field.name), element: field.element.nativeElement }))
      .filter(field => field.control.invalid)
      .forEach((field, index) => {
        index === 0 && isFocus && field.element.focus();
        field.control.markAsTouched();
      });
  }

  isEditMode() {
    return !!this.data.billingId;
  }

  getFormControl() {
    return this.form;
  }

  getFormBillingData(): Billing {
    const formData = this.form.getRawValue();
    const updatedData = {...this.data,
      billingName: formData.name.trim(),
      billingPhone: formData.phone.trim(),
      billingAddress: formData.address.trim(),
    };
    const billing: Billing = <Billing> updatedData;
    return billing;
  }

  addBilling$(billing: Billing = this.getFormBillingData()) {
    billing.isDefault = true;
    return this.userService.createBillingAddress(billing);
  }
  updateBilling$(billing: Billing = this.getFormBillingData()) {
    return this.userService.updateBillingAddress(billing);
  }

}



// WEBPACK FOOTER //
// ./src/app/components/billing-address/billing-form/billing-form.component.ts